import { useState } from "react";
import { Users, Plus, History, UserMinus } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { fmt } from "./WalletShared";

const inputCls = "border border-zinc-300 px-2 py-1.5 text-xs outline-none focus:ring-2 focus:ring-primary";

/* Members + permanent transaction ledger for one mileage pool (expands under the pool card). */
export function PoolDetails({ pool, onChange }) {
  const [addingMember, setAddingMember] = useState(false);
  const [member, setMember] = useState({ name: "", relationship: "", member_number: "", contributed: "" });
  const [addingTx, setAddingTx] = useState(false);
  const [tx, setTx] = useState({ member: "", miles_added: "", miles_used: "", purpose: "" });

  const saveMember = async () => {
    if (!member.name.trim()) { toast.error("Member name required"); return; }
    try {
      await api.post(`/wallet/pool/${pool.id}/members`, member);
      setMember({ name: "", relationship: "", member_number: "", contributed: "" });
      setAddingMember(false); onChange(); toast.success("Member added to pool");
    } catch { toast.error("Could not add member"); }
  };

  const removeMember = async (mid) => {
    await api.delete(`/wallet/pool/${pool.id}/members/${mid}`); onChange();
    toast.success("Member flagged removed — history kept");
  };

  const saveTx = async () => {
    if (!tx.miles_added && !tx.miles_used) { toast.error("Enter miles added or used"); return; }
    try {
      await api.post(`/wallet/pool/${pool.id}/ledger`, tx);
      setTx({ member: "", miles_added: "", miles_used: "", purpose: "" });
      setAddingTx(false); onChange(); toast.success("Transaction recorded");
    } catch { toast.error("Could not record transaction"); }
  };

  const members = pool.members || [];
  const ledger = [...(pool.ledger || [])].reverse();

  return (
    <div className="sm:col-span-2 lg:col-span-3 border border-rose-200 bg-rose-50/30 p-4" data-testid={`pool-details-${pool.id}`}>
      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-700 text-sm flex items-center gap-1.5"><Users className="w-4 h-4" /> Pooled members ({members.filter((m) => m.member_status !== "removed").length})</h4>
            <button onClick={() => setAddingMember((a) => !a)} data-testid={`pool-add-member-${pool.id}`} className="inline-flex items-center gap-1 text-xs text-primary font-600"><Plus className="w-3.5 h-3.5" /> Add member</button>
          </div>
          {addingMember && (
            <div className="grid grid-cols-2 gap-1.5 mb-2 bg-white border border-zinc-200 p-2" data-testid={`pool-member-form-${pool.id}`}>
              <input value={member.name} onChange={(e) => setMember({ ...member, name: e.target.value })} placeholder="Name" data-testid="pool-member-name" className={inputCls} />
              <input value={member.relationship} onChange={(e) => setMember({ ...member, relationship: e.target.value })} placeholder="Relationship" className={inputCls} />
              <input value={member.member_number} onChange={(e) => setMember({ ...member, member_number: e.target.value })} placeholder="Rewards number" className={inputCls} />
              <input value={member.contributed} onChange={(e) => setMember({ ...member, contributed: e.target.value })} type="number" placeholder="Miles contributed" className={inputCls} />
              <button onClick={saveMember} data-testid="pool-member-save" className="col-span-2 px-3 py-1.5 bg-primary text-white text-xs font-600">Save member</button>
            </div>
          )}
          {members.length === 0 ? <p className="text-xs text-muted-foreground">No members yet.</p> : (
            <ul className="space-y-1.5">
              {members.map((m) => (
                <li key={m.id} className={`flex items-center gap-2 text-xs bg-white border border-zinc-200 px-2 py-1.5 ${m.member_status === "removed" ? "opacity-50" : ""}`} data-testid={`pool-member-${m.id}`}>
                  <span className="font-600">{m.name}</span>
                  {m.relationship && <span className="text-muted-foreground">({m.relationship})</span>}
                  {m.contributed && <span className="ml-auto tabular-nums text-muted-foreground">{fmt(m.contributed)} contributed</span>}
                  {m.member_status === "removed"
                    ? <span className="text-rose-600 font-600">removed</span>
                    : <button onClick={() => removeMember(m.id)} title="Remove from pool (history kept)" data-testid={`pool-member-remove-${m.id}`} className="text-muted-foreground hover:text-destructive"><UserMinus className="w-3.5 h-3.5" /></button>}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-700 text-sm flex items-center gap-1.5"><History className="w-4 h-4" /> Transaction ledger</h4>
            <button onClick={() => setAddingTx((a) => !a)} data-testid={`pool-add-tx-${pool.id}`} className="inline-flex items-center gap-1 text-xs text-primary font-600"><Plus className="w-3.5 h-3.5" /> Record transaction</button>
          </div>
          {addingTx && (
            <div className="grid grid-cols-2 gap-1.5 mb-2 bg-white border border-zinc-200 p-2" data-testid={`pool-tx-form-${pool.id}`}>
              <input value={tx.member} onChange={(e) => setTx({ ...tx, member: e.target.value })} placeholder="Member (optional)" className={inputCls} />
              <input value={tx.purpose} onChange={(e) => setTx({ ...tx, purpose: e.target.value })} placeholder="Purpose / trip" className={inputCls} />
              <input value={tx.miles_added} onChange={(e) => setTx({ ...tx, miles_added: e.target.value })} type="number" placeholder="Miles added" data-testid="pool-tx-added" className={inputCls} />
              <input value={tx.miles_used} onChange={(e) => setTx({ ...tx, miles_used: e.target.value })} type="number" placeholder="Miles used" className={inputCls} />
              <button onClick={saveTx} data-testid="pool-tx-save" className="col-span-2 px-3 py-1.5 bg-primary text-white text-xs font-600">Record (permanent — never overwritten)</button>
            </div>
          )}
          {ledger.length === 0 ? <p className="text-xs text-muted-foreground">No transactions recorded yet.</p> : (
            <div className="max-h-48 overflow-y-auto border border-zinc-200 bg-white">
              <table className="w-full text-[11px]">
                <thead className="bg-zinc-50 text-muted-foreground sticky top-0"><tr>
                  <th className="text-left px-2 py-1">Date</th><th className="text-left px-2 py-1">Member</th>
                  <th className="text-right px-2 py-1">+/−</th><th className="text-right px-2 py-1">Balance</th><th className="text-left px-2 py-1">Purpose</th>
                </tr></thead>
                <tbody>
                  {ledger.map((e) => (
                    <tr key={e.id} className="border-t border-zinc-100" data-testid={`pool-tx-${e.id}`}>
                      <td className="px-2 py-1 font-mono">{e.date}</td>
                      <td className="px-2 py-1">{e.member || "—"}</td>
                      <td className={`px-2 py-1 text-right tabular-nums font-600 ${e.miles_used ? "text-rose-600" : "text-emerald-700"}`}>{e.miles_used ? `−${fmt(e.miles_used)}` : `+${fmt(e.miles_added)}`}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{fmt(e.balance)}</td>
                      <td className="px-2 py-1 text-muted-foreground">{e.purpose || e.type || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* Pool card add-on: toggle to expand members & ledger under the pool card. */
export default function PoolExtra({ pool, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen((o) => !o)} data-testid={`pool-toggle-${pool.id}`}
        className="sm:col-span-2 lg:col-span-3 -mt-2 text-left text-xs font-700 text-rose-700 hover:underline inline-flex items-center gap-1">
        <Users className="w-3.5 h-3.5" /> {open ? "Hide" : "Show"} members & transaction ledger — {pool.pool_name || pool.airline}
      </button>
      {open && <PoolDetails pool={pool} onChange={onChange} />}
    </>
  );
}
